import { useEffect, useRef, useState } from 'react';
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { RelatedLinks, type RelatedCode, type RelatedScenario } from './RelatedLinks';
import { DignityNarrationPlayer } from './DignityNarrationPlayer';
import { Bot, Stethoscope, DollarSign, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import EducationalContentRenderer from '@/components/EducationalContentRenderer';
import { fillCostTemplate, type CostTemplateContext } from '../../../functions/lib/cost-template';

// TypeScript declaration for <model-viewer> web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        "auto-rotate"?: boolean | string;
        "camera-controls"?: boolean | string;
        "shadow-intensity"?: string;
        exposure?: string;
        "interaction-prompt"?: string;
        "camera-orbit"?: string;
      };
    }
  }
}

interface DignityEducationTabProps {
  // For scenarios — AI content fields
  scenarioId?: string;
  aiConsumerMessage?: string;
  aiClinicalSummary?: string;
  aiCostAnalysis?: string;
  aiPreparationGuide?: string;
  // For code pages — educational content
  educationalHtml?: string;
  llmSummary?: string;
  procedureCode?: string;
  codeType?: string;
  // Cross-links
  relatedCodes?: RelatedCode[];
  relatedScenarios?: RelatedScenario[];
  // Context
  title: string;
  /** Live numbers for {{tokens}} in the AI sections (cost-template.ts) — pass scenarioContext(scenario) / codeContext(data). */
  templateContext?: CostTemplateContext;
}

const AI_SECTIONS = [
  { key: 'consumer', field: 'aiConsumerMessage' as const, labelKey: 'scenarios:dignity.whatYouNeed', icon: Bot, color: '#002868' },
  { key: 'clinical', field: 'aiClinicalSummary' as const, labelKey: 'scenarios:dignity.medicalJourney', icon: Stethoscope, color: '#2b5e49' },
  { key: 'cost', field: 'aiCostAnalysis' as const, labelKey: 'scenarios:dignity.whereMoneyGoes', icon: DollarSign, color: '#B22234' },
  { key: 'prep', field: 'aiPreparationGuide' as const, labelKey: 'scenarios:dignity.howToPrepare', icon: ClipboardList, color: '#C5A55A' },
];

export function DignityEducationTab({
  scenarioId,
  aiConsumerMessage,
  aiClinicalSummary,
  aiCostAnalysis,
  aiPreparationGuide,
  templateContext,
  educationalHtml,
  llmSummary,
  procedureCode,
  codeType,
  relatedCodes,
  relatedScenarios,
  title,
}: DignityEducationTabProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  // Theme tokens matching homepage
  const T = {
    blue: '#002868',
    red: '#B22234',
    gold: '#C5A55A',
    green: '#2b5e49',
    text: isDark ? '#f0ebe3' : '#1B1B1B',
    textMuted: isDark ? '#a0998e' : '#6b6560',
    cardBg: isDark ? '#0f1d2f' : '#ffffff',
    panelBg: isDark ? 'rgba(0,20,60,0.94)' : 'rgba(255,255,255,0.97)',
    panelBorder: isDark ? `2px solid #C5A55A` : '2px solid #002868',
    sectionBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,40,104,0.02)',
    sectionBorder: isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,40,104,0.12)',
  };

  // Stored prose may carry {{tokens}}; fill them from the page's own numbers so the
  // text can never disagree with the price band beside it (see cost-template.ts).
  const fill = (t?: string) => (t && templateContext ? fillCostTemplate(t, templateContext) : t);
  const aiContent: Record<string, string | undefined> = {
    aiConsumerMessage: fill(aiConsumerMessage),
    aiClinicalSummary: fill(aiClinicalSummary),
    aiCostAnalysis: fill(aiCostAnalysis),
    aiPreparationGuide: fill(aiPreparationGuide),
  };

  const hasAiContent = Object.values(aiContent).some(v => v && v.length > 0);
  const hasEducational = !!(educationalHtml || llmSummary);
  const hasContent = hasAiContent || hasEducational || !!relatedCodes?.length || !!relatedScenarios?.length;

  // Expandable sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['consumer']));
  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // 3D model-viewer loading — only if WebGL is available
  const viewerRef = useRef<HTMLElement>(null);
  const [modelReady, setModelReady] = useState(false);
  const animRef = useRef<number>(0);
  const hasWebGL = typeof window !== 'undefined' && (() => {
    try {
      const c = document.createElement('canvas');
      const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null;
      if (!gl) return false;
      // Check for software renderer (llvmpipe) which crashes model-viewer
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
        if (/llvmpipe|swiftshader|software/i.test(renderer)) return false;
      }
      return true;
    } catch { return false; }
  })();
  const useGlb = typeof window !== 'undefined' && window.innerWidth >= 768 && hasWebGL;

  useEffect(() => {
    if (!useGlb) return;
    const load = async () => {
      try {
        if (!(typeof customElements !== 'undefined' && customElements.get('model-viewer'))) {
          await ensureModelViewer();
          await customElements.whenDefined('model-viewer');
        }
        // Test if model-viewer can actually create a renderer (catches xr/WebGL crashes)
        const testEl = document.createElement('model-viewer') as any;
        testEl.style.cssText = 'width:1px;height:1px;position:absolute;left:-9999px';
        document.body.appendChild(testEl);
        await new Promise(r => setTimeout(r, 200));
        document.body.removeChild(testEl);
        setModelReady(true);
      } catch { /* PNG fallback will show */ }
    };
    load();
  }, [useGlb]);

  // Gentle head oscillation
  useEffect(() => {
    if (!useGlb || !modelReady) return;
    const amplitude = 15;
    const period = 5000;
    const start = performance.now();
    function animate(now: number) {
      const elapsed = now - start;
      const theta = amplitude * Math.sin((2 * Math.PI * elapsed) / period);
      const viewer = viewerRef.current as any;
      if (viewer && viewer.cameraOrbit !== undefined) {
        viewer.cameraOrbit = `${theta}deg 75deg 105%`;
      }
      animRef.current = requestAnimationFrame(animate);
    }
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [useGlb, modelReady]);

  if (!hasContent) return null;

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* CSS for floating animation */}
      <style>{`
        @keyframes dignityFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .dignity-section-float { animation: dignityFloat 4.2s ease-in-out infinite; }
      `}</style>

      {/* ── Main Container ── */}
      <div style={{
        background: isDark
          ? 'linear-gradient(135deg, #0a1628 0%, #0f1d2f 50%, #0a1628 100%)'
          : 'linear-gradient(135deg, #f8f6f1 0%, #ffffff 50%, #f8f6f1 100%)',
        border: T.panelBorder,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: isDark
          ? '0 8px 40px rgba(0,20,60,0.5), inset 0 1px 0 rgba(197,165,90,0.15)'
          : '0 8px 40px rgba(0,20,60,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
      }}>

        {/* ── Nameplate Header ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          background: `linear-gradient(90deg, ${T.blue} 0%, #003A9E 100%)`,
          borderBottom: `2px solid ${T.gold}`,
        }}>
          <span style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 800,
            color: T.gold,
            letterSpacing: '3px',
            textTransform: 'uppercase',
          }}>
            ✦ {t('scenarios:dignity.guideLabel')}
          </span>
          <span style={{
            fontFamily: sans,
            fontSize: 10,
            color: 'rgba(197,165,90,0.6)',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}>
            {t('scenarios:dignity.guideSubtitle')}
          </span>
        </div>

        {/* ── Hero: Dignity model + intro ── */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 24px 0',
        }}>
          {/* Dignity portrait — GLB model on desktop, PNG fallback */}
          <div className="dignity-section-float" style={{ marginBottom: 8 }}>
            {useGlb && modelReady ? (
              <model-viewer
                ref={viewerRef as any}
                src="/consumer/dignity_bill.glb"
                alt="Dignity — your AI healthcare guide"
                shadow-intensity="0.8"
                exposure="1.1"
                interaction-prompt="none"
                camera-orbit="0deg 75deg 105%"
                style={{
                  width: 'clamp(160px, 22vw, 220px)',
                  height: 'clamp(180px, 26vw, 260px)',
                  background: 'transparent',
                  display: 'block',
                } as React.CSSProperties}
              />
            ) : (
              <div style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                overflow: 'hidden',
                background: isDark ? 'rgba(197,165,90,0.1)' : '#FFF8E7',
                border: `3px solid ${T.gold}`,
                boxShadow: `0 4px 20px rgba(197,165,90,0.25)`,
              }}>
                <img
                  src="/consumer/dignity-transparent.webp"
                  alt="Dignity"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                />
              </div>
            )}
          </div>

          {/* Title */}
          <h3 style={{
            fontFamily: serif,
            fontSize: 'clamp(22px, 3vw, 32px)',
            fontWeight: 400,
            color: T.text,
            textAlign: 'center',
            lineHeight: 1.15,
            marginBottom: 6,
            letterSpacing: '-0.5px',
          }}>
            {title}
          </h3>
          <p style={{
            fontFamily: serif,
            fontSize: 'clamp(13px, 1.5vw, 16px)',
            color: isDark ? T.gold : T.blue,
            fontStyle: 'italic',
            textAlign: 'center',
            marginBottom: 12,
          }}>
            {t('scenarios:dignity.walkthrough')}
          </p>
        </div>

          {/* Narration voice player — visual novel dialogue experience */}
          <div style={{ padding: '0 20px 20px', width: '100%', boxSizing: 'border-box' }}>
            <DignityNarrationPlayer
              scenarioId={scenarioId}
              procedureCode={procedureCode}
              codeType={codeType}
            />
          </div>

        {/* ── Content Body ── */}
        <div style={{ padding: '0 24px 24px' }}>

          {/* AI Content Sections (scenarios) */}
          {hasAiContent && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: relatedCodes?.length || hasEducational ? 24 : 0 }}>
              {AI_SECTIONS.map(section => {
                const text = aiContent[section.field];
                if (!text) return null;
                const isExpanded = expandedSections.has(section.key);
                const Icon = section.icon;

                return (
                  <div
                    key={section.key}
                    style={{
                      borderRadius: 10,
                      border: `1px solid ${T.sectionBorder}`,
                      overflow: 'hidden',
                      background: T.sectionBg,
                    }}
                  >
                    <button
                      onClick={() => toggleSection(section.key)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        background: 'transparent',
                        border: 'none',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,40,104,0.04)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: `${section.color}15`,
                        color: section.color,
                        flexShrink: 0,
                      }}>
                        <Icon style={{ width: 14, height: 14 }} />
                      </span>
                      <span style={{
                        fontFamily: sans,
                        fontSize: 14,
                        fontWeight: 600,
                        color: T.text,
                        flex: 1,
                      }}>
                        {t(section.labelKey)}
                      </span>
                      {isExpanded
                        ? <ChevronUp style={{ width: 16, height: 16, color: T.textMuted }} />
                        : <ChevronDown style={{ width: 16, height: 16, color: T.textMuted }} />
                      }
                    </button>
                    {isExpanded && (
                      <div style={{
                        padding: '0 18px 14px',
                        marginLeft: 14,
                        paddingLeft: 22,
                        borderLeft: `3px solid ${section.color}`,
                        fontFamily: sans,
                        fontSize: 14,
                        lineHeight: 1.7,
                        color: T.textMuted,
                        whiteSpace: 'pre-line',
                      }}>
                        {text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* LLM Summary (code pages) */}
          {llmSummary && (
            <div style={{
              padding: '16px 18px',
              borderRadius: 10,
              border: `1px solid ${T.sectionBorder}`,
              background: T.sectionBg,
              marginBottom: educationalHtml ? 16 : (relatedScenarios?.length ? 24 : 0),
            }}>
              <p style={{
                fontFamily: sans,
                fontSize: 15,
                lineHeight: 1.75,
                color: T.text,
                margin: 0,
              }}>
                {llmSummary}
              </p>
            </div>
          )}

          {/* Educational HTML (code pages) */}
          {educationalHtml && procedureCode && codeType && (
            <div style={{
              padding: '16px 18px',
              borderRadius: 10,
              border: `1px solid ${T.sectionBorder}`,
              background: T.sectionBg,
              marginBottom: relatedScenarios?.length ? 24 : 0,
            }}>
              <div className={`prose ${isAhp ? 'prose-stone' : 'prose-invert'} max-w-none [&>*]:break-words [&>*]:overflow-wrap-anywhere`}>
                <EducationalContentRenderer
                  htmlContent={educationalHtml}
                  procedureCode={procedureCode}
                  codeType={codeType}
                />
              </div>
            </div>
          )}

          {/* Cross-links */}
          {(relatedCodes?.length || relatedScenarios?.length) ? (
            <div style={{
              padding: '16px 18px',
              borderRadius: 10,
              border: `1px solid ${T.sectionBorder}`,
              background: T.sectionBg,
            }}>
              <RelatedLinks relatedCodes={relatedCodes} relatedScenarios={relatedScenarios} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
